/**
 * Math Sprint and the culture quiz.
 *
 * The quiz questions are generated from a fixed bank rather than fetched, so
 * the arcade has no runtime dependency on anything outside itself: no key, no
 * quota, and no game that breaks because a third party changed a response
 * shape. The maths questions are generated outright, which means they never run
 * out and never repeat in the same order twice.
 */
import { byLevel } from '@arena/games';

import { Lifecycle, h, shuffle, tone } from '../kit.js';
import type { GameDef } from '../types.js';

function pill(label: string, value: string): [HTMLElement, HTMLElement] {
  const box = h('div', 'ac-pill');
  const strong = h('strong', '', value);
  box.append(h('span', '', label), strong);
  return [box, strong];
}

// -------------------------------------------------------------- math sprint

export const mathSprint: GameDef = {
  id: 'math-sprint',
  title: 'Math Sprint',
  blurb: 'Un maximum de calculs en soixante secondes.',
  category: 'Maths',
  difficulty: 'Moyen',
  icon: '\u{1F9EE}',
  unit: 'points',
  higherIsBetter: true,
  how: 'Soixante secondes. Les calculs deviennent plus durs a mesure que tu enchaines, et une erreur coute cinq secondes.',
  mount(root, ctx) {
    const life = new Lifecycle();
    const [scoreBox, scoreValue] = pill('Score', '0');
    const [timeBox, timeValue] = pill('Temps', '60');
    const [streakBox, streakValue] = pill('Serie', '0');
    const hud = h('div', 'ac-hud');
    hud.append(scoreBox, timeBox, streakBox);

    const question = h('p', 'ac-big', '');
    question.style.cssText = 'font-size:clamp(30px,8vw,54px);margin:18px 0';

    const options = h('div');
    options.style.cssText =
      'display:grid;grid-template-columns:repeat(2,1fr);gap:10px;width:100%;max-width:400px';

    /*
     * Difficulty is how fast the sums grow and what a mistake costs.
     *
     * The sixty seconds are the same at every level, deliberately: the score is
     * how many you got through, and a longer clock would just be a bigger
     * number rather than an easier game. What changes is where the ramp tops
     * out, how many right answers it takes to climb a step, and the penalty.
     */
    const RAMP_TOP = byLevel(ctx.level, 3, 6, 8);
    const RAMP_EVERY = byLevel(ctx.level, 5, 3, 2);
    const PENALTY_MS = byLevel(ctx.level, 3000, 5000, 7000);

    let score = 0;
    let streak = 0;
    let answer = 0;
    let endsAt = 0;
    let running = false;

    /** Difficulty rises with the streak, so a good run gets genuinely harder. */
    const makeQuestion = (): void => {
      const level = Math.min(RAMP_TOP, Math.floor(streak / RAMP_EVERY));
      const kinds = level < 2 ? ['+', '-'] : level < 4 ? ['+', '-', '×'] : ['+', '-', '×', '÷'];
      const op = kinds[Math.floor(Math.random() * kinds.length)];
      const span = 8 + level * 9;

      let a = 1 + Math.floor(Math.random() * span);
      let b = 1 + Math.floor(Math.random() * span);
      if (op === '×') {
        a = 2 + Math.floor(Math.random() * (4 + level * 2));
        b = 2 + Math.floor(Math.random() * (4 + level * 2));
        answer = a * b;
      } else if (op === '÷') {
        // Build the division from its answer so it always divides exactly.
        b = 2 + Math.floor(Math.random() * (3 + level));
        answer = 2 + Math.floor(Math.random() * (5 + level * 2));
        a = b * answer;
      } else if (op === '-') {
        if (b > a) [a, b] = [b, a]; // keep it positive, this is a speed test
        answer = a - b;
      } else {
        answer = a + b;
      }

      question.textContent = `${a} ${op} ${b}`;

      // Three wrong answers that are plausible, not absurd: near misses force
      // actual arithmetic rather than eyeballing the magnitude.
      const wrong = new Set<number>();
      while (wrong.size < 3) {
        const delta = Math.max(1, Math.round((Math.random() - 0.5) * Math.max(4, answer * 0.4)));
        const candidate = answer + (delta === 0 ? 1 : delta);
        if (candidate !== answer && candidate >= 0) wrong.add(candidate);
      }

      options.replaceChildren();
      for (const value of shuffle([answer, ...wrong])) {
        const btn = h('button', 'ac-primary', String(value));
        btn.style.cssText = 'padding:18px 0;font-size:21px';
        btn.addEventListener('click', () => choose(value));
        options.append(btn);
      }
    };

    const choose = (value: number): void => {
      if (!running) return;
      if (value === answer) {
        streak++;
        score += 10 + streak * 2;
        tone(700, 55, 'triangle');
      } else {
        streak = 0;
        // Time, not points: losing points can leave a score stuck at zero and
        // kill the run, losing seconds keeps the pressure without the despair.
        endsAt -= PENALTY_MS;
        tone(180, 140, 'square');
      }
      scoreValue.textContent = String(score);
      streakValue.textContent = String(streak);
      makeQuestion();
    };

    const start = (): void => {
      running = true;
      score = 0;
      streak = 0;
      scoreValue.textContent = '0';
      streakValue.textContent = '0';
      endsAt = performance.now() + 60_000;
      startBtn.hidden = true;
      options.hidden = false;
      makeQuestion();
    };

    life.frame(() => {
      if (!running) return;
      const left = Math.max(0, (endsAt - performance.now()) / 1000);
      timeValue.textContent = String(Math.ceil(left));
      if (left <= 0) {
        running = false;
        ctx.submit(score);
        question.textContent = `${score} points`;
        options.hidden = true;
        startBtn.hidden = false;
        startBtn.textContent = 'Rejouer';
      }
    });

    const startBtn = h('button', 'ac-primary', 'Commencer');
    startBtn.addEventListener('click', start);
    options.hidden = true;

    root.append(hud, question, options, startBtn, h('p', 'ac-note', 'Une erreur coute cinq secondes.'));
    return () => life.stop();
  },
};

// ---------------------------------------------------------------------- quiz

interface Question {
  q: string;
  answers: string[];
  correct: number;
}

const BANK: Question[] = [
  { q: 'Quelle est la capitale de l’Australie ?', answers: ['Sydney', 'Canberra', 'Melbourne', 'Perth'], correct: 1 },
  { q: 'Combien de cotes a un hexagone ?', answers: ['5', '6', '7', '8'], correct: 1 },
  { q: 'Quelle planete est la plus proche du Soleil ?', answers: ['Venus', 'Mars', 'Mercure', 'Terre'], correct: 2 },
  { q: 'Qui a peint la Joconde ?', answers: ['Raphael', 'Michel Ange', 'Leonard de Vinci', 'Botticelli'], correct: 2 },
  { q: 'Quel est le plus grand ocean ?', answers: ['Atlantique', 'Indien', 'Arctique', 'Pacifique'], correct: 3 },
  { q: 'En quelle annee a eu lieu la Revolution francaise ?', answers: ['1769', '1789', '1799', '1809'], correct: 1 },
  { q: 'Quel gaz les plantes absorbent elles ?', answers: ['Oxygene', 'Azote', 'Dioxyde de carbone', 'Hydrogene'], correct: 2 },
  { q: 'Combien de joueurs dans une équipe de football sur le terrain ?', answers: ['9', '10', '11', '12'], correct: 2 },
  { q: 'Quel est le plus long fleuve du monde ?', answers: ['Amazone', 'Nil', 'Yangtse', 'Mississippi'], correct: 1 },
  { q: 'Quelle est la monnaie du Japon ?', answers: ['Yuan', 'Won', 'Yen', 'Bath'], correct: 2 },
  { q: 'Combien d’os compte le corps humain adulte ?', answers: ['186', '206', '226', '246'], correct: 1 },
  { q: 'Quel element chimique a pour symbole Fe ?', answers: ['Fluor', 'Fer', 'Francium', 'Fermium'], correct: 1 },
  { q: 'Quel pays a invente le the vert matcha ?', answers: ['Chine', 'Japon', 'Inde', 'Coree'], correct: 0 },
  { q: 'Quelle est la plus haute montagne du monde ?', answers: ['K2', 'Everest', 'Kangchenjunga', 'Mont Blanc'], correct: 1 },
  { q: 'Combien de touches a un piano standard ?', answers: ['76', '82', '88', '92'], correct: 2 },
  { q: 'Qui a ecrit Les Miserables ?', answers: ['Zola', 'Balzac', 'Hugo', 'Flaubert'], correct: 2 },
  { q: 'Quelle est la vitesse de la lumiere approximative ?', answers: ['300 km/s', '3 000 km/s', '300 000 km/s', '3 000 000 km/s'], correct: 2 },
  { q: 'Quel animal est le plus rapide sur terre ?', answers: ['Lion', 'Guepard', 'Antilope', 'Cheval'], correct: 1 },
  { q: 'Combien de continents y a t il ?', answers: ['5', '6', '7', '8'], correct: 2 },
  { q: 'Quelle mer borde la France au sud ?', answers: ['Mer du Nord', 'Mer Baltique', 'Mer Mediterranee', 'Mer Noire'], correct: 2 },
];

export const quiz: GameDef = {
  id: 'quiz',
  title: 'Quiz Culture',
  blurb: 'Dix questions de culture generale.',
  category: 'Culture',
  difficulty: 'Moyen',
  icon: '\u{1F393}',
  unit: '/ 10',
  higherIsBetter: true,
  how: 'Dix questions tirees au hasard. Une seule bonne reponse par question, et pas de seconde chance.',
  mount(root, ctx) {
    const life = new Lifecycle();
    const ROUNDS = 10;

    /*
     * Difficulty here is the shape of the question, not the question itself.
     *
     * A harder BANK would be the obvious knob and is the wrong one: it would
     * mean three separate sets of trivia to write and keep balanced, and a
     * child would meet a completely different quiz rather than the same one
     * asked more sharply. So easy drops the distractors down to one, and hard
     * puts a clock on each question. Moyen is the quiz exactly as it was, which
     * is what keeps every record already on that board meaningful.
     */
    const CHOICES = byLevel(ctx.level, 2, 4, 4);
    const THINK_MS = byLevel(ctx.level, 0, 0, 12_000);

    const [progressBox, progressValue] = pill('Question', '1 / 10');
    const [scoreBox, scoreValue] = pill('Bonnes', '0');
    const hud = h('div', 'ac-hud');
    hud.append(progressBox, scoreBox);

    let deck: Question[] = [];
    let index = 0;
    let correct = 0;
    let locked = false;
    /** The per question countdown, armed only in hard mode. */
    let deadline = 0;

    const question = h('p', 'ac-big', '');
    question.style.cssText = 'font-size:clamp(19px,4.2vw,27px);margin:16px 0;max-width:640px';
    const clockLine = h('p', 'ac-note', '');

    const options = h('div');
    options.style.cssText = 'display:grid;gap:10px;width:100%;max-width:520px';

    const show = (): void => {
      locked = false;
      const item = deck[index];
      progressValue.textContent = `${index + 1} / ${ROUNDS}`;
      question.textContent = item.q;
      options.replaceChildren();

      // Shuffle the ANSWERS too, not just the questions. A fixed order means the
      // right answer sits in the same slot every time you replay a question.
      let order = shuffle(item.answers.map((text, i) => ({ text, i })));
      if (CHOICES < order.length) {
        // Keep the right answer and enough wrong ones to make the choice real,
        // then shuffle again so the survivor is not always in the same place.
        const right = order.find((o) => o.i === item.correct)!;
        const wrong = order.filter((o) => o.i !== item.correct).slice(0, CHOICES - 1);
        order = shuffle([right, ...wrong]);
      }
      /**
       * Reveal the answer and move on.
       *
       * Shared by answering and by running out of time, so a question that
       * timed out is shown exactly the way a wrong answer is: the right one
       * lights up, and you see it before the next question arrives. Silently
       * skipping ahead would teach nothing and read as a bug.
       */
      const reveal = (chosen: HTMLButtonElement | null, right: boolean): void => {
        if (locked) return;
        locked = true;
        window.clearTimeout(deadline);
        if (right) {
          correct++;
          scoreValue.textContent = String(correct);
          tone(760, 90, 'triangle');
        } else {
          tone(180, 180, 'square');
        }
        for (const child of options.children) {
          const el = child as HTMLButtonElement;
          const isRight = el.textContent === item.answers[item.correct];
          el.style.background = isRight
            ? 'rgba(34,197,94,0.24)'
            : el === chosen
              ? 'rgba(239,68,68,0.24)'
              : 'transparent';
          el.style.borderColor = isRight ? '#22c55e' : el === chosen ? '#ef4444' : '#2a1c47';
          el.disabled = true;
        }
        life.timeout(() => {
          index++;
          if (index >= ROUNDS) {
            ctx.submit(correct);
            question.textContent = `${correct} bonnes reponses sur ${ROUNDS}`;
            options.replaceChildren();
            clockLine.textContent = '';
            startBtn.hidden = false;
            startBtn.textContent = 'Rejouer';
            return;
          }
          show();
        }, 900);
      };

      // Hard mode puts a clock on each question. At the other levels THINK_MS
      // is zero and no timer is ever armed, so the quiz is untouched.
      if (THINK_MS > 0) {
        let left = THINK_MS / 1000;
        clockLine.textContent = `${left}s`;
        const tick = (): void => {
          left--;
          clockLine.textContent = `${Math.max(0, left)}s`;
          if (left > 0) deadline = life.timeout(tick, 1000);
          else reveal(null, false);
        };
        deadline = life.timeout(tick, 1000);
      }

      for (const option of order) {
        const btn = h('button', 'ac-ghost', option.text);
        btn.style.cssText = 'padding:14px 16px;font-size:15.5px;text-align:left';
        btn.addEventListener('click', () => {
          reveal(btn, option.i === item.correct);
        });
        options.append(btn);
      }
    };

    const start = (): void => {
      deck = shuffle([...BANK]).slice(0, ROUNDS);
      index = 0;
      correct = 0;
      scoreValue.textContent = '0';
      startBtn.hidden = true;
      show();
    };

    const startBtn = h('button', 'ac-primary', 'Commencer');
    startBtn.addEventListener('click', start);

    root.append(hud, question, clockLine, options, startBtn);
    return () => life.stop();
  },
};
